import React from 'react';
import { Check, ArrowRight, Presentation, Clock, Users, Lightbulb, Mic, MessageCircle } from 'lucide-react';
import { PageTransition } from '../components/layout/PageTransition';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { DecorativeShape } from '../components/DecorativeShape';
import { Animate } from '../components/ui/Animate';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
export function Talks() {
  const {
    t
  } = useLanguage();
  const talks = [{
    id: 'short',
    icon: <Lightbulb size={32} data-id="element-865" />,
    color: 'bg-coral',
    lightColor: 'bg-coral/10',
    textColor: 'text-coral',
    borderColor: 'border-coral/20',
    tKey: 'short'
  }, {
    id: 'standard',
    icon: <Presentation size={32} data-id="element-866" />,
    color: 'bg-teal',
    lightColor: 'bg-teal/10',
    textColor: 'text-teal',
    borderColor: 'border-teal/20',
    tKey: 'standard'
  }, {
    id: 'deep-dive',
    icon: <MessageCircle size={32} data-id="element-867" />,
    color: 'bg-blue-600',
    lightColor: 'bg-blue-50',
    textColor: 'text-blue-600',
    borderColor: 'border-blue-200',
    tKey: 'deepDive'
  }];
  const topicIcons = [<Users size={24} data-id="element-868" />, <MessageCircle size={24} data-id="element-869" />, <Lightbulb size={24} data-id="element-870" />, <Presentation size={24} data-id="element-871" />];
  const topicKeys = ['leadership', 'teams', 'agile', 'creative'];
  return <PageTransition data-id="element-872">
      <div className="pt-12 pb-24" data-id="element-873">
        <section className="text-center max-w-4xl mx-auto px-4 mb-20 relative" data-id="element-874">
          <DecorativeShape type="square" color="bg-teal/10" className="top-0 right-0 w-32 h-32 rotate-12 -z-10" data-id="element-875" />
          <DecorativeShape type="circle" color="bg-coral/10" className="bottom-0 left-0 w-40 h-40 -z-10" data-id="element-876" />
          <Animate data-id="element-877">
            <span className="inline-block px-4 py-2 bg-teal/10 text-teal font-medium rounded-full text-sm mb-6" data-id="element-878">
              {t('talks.badge')}
            </span>
            <h1 className="text-5xl md:text-6xl font-bold mb-6" data-id="element-879">
              {t('talks.title')} <br data-id="element-880" />
              <span className="text-teal" data-id="element-881">{t('talks.titleHighlight')}</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-882">
              {t('talks.subtitle')}
            </p>
          </Animate>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24" data-id="element-883">
          <Animate className="text-center mb-12" data-id="element-884">
            <h2 className="text-3xl font-bold text-charcoal mb-4" data-id="element-885">
              {t('talks.chooseTitle')}
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto" data-id="element-886">
              {t('talks.chooseSubtitle')}
            </p>
          </Animate>

          <div className="space-y-8" data-id="element-887">
            {talks.map((talk, index) => {
            const topics = t(`talks.items.${talk.tKey}.topics`) as string[];
            const format = t(`talks.items.${talk.tKey}.format`) as string[];
            return <Animate key={talk.id} delay={0} data-id="element-888">
                  <Card className={`p-0 overflow-hidden border-2 ${talk.borderColor}`} data-id="element-889">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-0" data-id="element-890">
                      <div className={`lg:col-span-4 ${talk.lightColor} p-10 flex flex-col justify-center items-center text-center relative overflow-hidden`} data-id="element-891">
                        <div className={`w-20 h-20 rounded-full ${talk.color} text-white flex items-center justify-center mb-6 shadow-lg relative z-10`} data-id="element-892">
                          {talk.icon}
                        </div>
                        <h3 className={`text-2xl font-bold ${talk.textColor} mb-2 relative z-10`} data-id="element-893">
                          {t(`talks.items.${talk.tKey}.title`)}
                        </h3>
                        <div className="flex items-center gap-2 mb-2 relative z-10" data-id="element-894">
                          <Clock size={18} className={talk.textColor} data-id="element-895" />
                          <span className={`font-bold ${talk.textColor}`} data-id="element-896">
                            {t(`talks.items.${talk.tKey}.duration`)}
                          </span>
                        </div>
                        <p className="font-medium text-gray-600 relative z-10" data-id="element-897">
                          {t(`talks.items.${talk.tKey}.subtitle`)}
                        </p>
                      </div>
                      <div className="lg:col-span-8 p-8 md:p-12 flex flex-col justify-center bg-white" data-id="element-898">
                        <p className="text-lg text-gray-600 mb-6 leading-relaxed" data-id="element-899">
                          {t(`talks.items.${talk.tKey}.description`)}
                        </p>
                        <div className="mb-6 p-4 bg-gray-50 rounded-xl" data-id="element-900">
                          <p className="text-sm font-medium text-gray-500 mb-1" data-id="element-901">
                            {t('talks.idealFor')}
                          </p>
                          <p className="text-gray-700" data-id="element-902">
                            {t(`talks.items.${talk.tKey}.idealFor`)}
                          </p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-903">
                          <div data-id="element-904">
                            <p className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3" data-id="element-905">
                              {t('talks.topicsLabel')}
                            </p>
                            <div className="space-y-2" data-id="element-906">
                              {topics.map((topic: string, i: number) => <div key={i} className="flex items-center" data-id="element-907">
                                  <div className={`mr-3 p-1 rounded-full ${talk.lightColor} ${talk.textColor}`} data-id="element-908">
                                    <Check size={12} strokeWidth={3} data-id="element-909" />
                                  </div>
                                  <span className="text-gray-700" data-id="element-910">{topic}</span>
                                </div>)}
                            </div>
                          </div>
                          <div data-id="element-911">
                            <p className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3" data-id="element-912">
                              {t('talks.formatLabel')}
                            </p>
                            <div className="space-y-2" data-id="element-913">
                              {format.map((item: string, i: number) => <div key={i} className="flex items-start" data-id="element-914">
                                  <div className={`mt-1 mr-3 p-1 rounded-full ${talk.lightColor} ${talk.textColor}`} data-id="element-915">
                                    <Check size={12} strokeWidth={3} data-id="element-916" />
                                  </div>
                                  <span className="text-gray-700 text-sm" data-id="element-917">
                                    {item}
                                  </span>
                                </div>)}
                            </div>
                          </div>
                        </div>
                        <div className="pt-6 border-t border-gray-100" data-id="element-918">
                          <Link to="/contact" data-id="element-919">
                            <Button variant={index === 1 ? 'secondary' : 'primary'} rightIcon={<ArrowRight size={18} data-id="element-921" />} data-id="element-920">
                              {t('talks.bookTalk')}
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </Card>
                </Animate>;
          })}
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24" data-id="element-922">
          <Animate className="text-center mb-12" data-id="element-923">
            <h2 className="text-3xl font-bold text-charcoal mb-4" data-id="element-924">
              {t('talks.popularTopics')}
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto" data-id="element-925">
              {t('talks.popularTopicsSubtitle')}
            </p>
          </Animate>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-926">
            {topicKeys.map((key, index) => <Animate key={key} delay={index * 0.1} data-id="element-927">
                <Card className="p-6 text-center h-full border-2 border-gray-100" data-id="element-928">
                  <div className="w-14 h-14 mx-auto mb-4 bg-gray-100 rounded-2xl flex items-center justify-center text-gray-600" data-id="element-929">
                    {topicIcons[index]}
                  </div>
                  <h3 className="font-bold text-charcoal mb-2" data-id="element-930">
                    {t(`talks.topicsList.${key}.title`)}
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-931">
                    {t(`talks.topicsList.${key}.description`)}
                  </p>
                </Card>
              </Animate>)}
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 text-center" data-id="element-932">
          <Animate data-id="element-933">
            <Card className="p-12 bg-gradient-to-br from-teal/5 to-coral/5 border-0" data-id="element-934">
              <div className="w-16 h-16 mx-auto mb-6 bg-teal/10 rounded-full flex items-center justify-center" data-id="element-935">
                <Mic size={32} className="text-teal" data-id="element-936" />
              </div>
              <h2 className="text-3xl font-bold mb-4" data-id="element-937">
                {t('talks.customTitle')}
              </h2>
              <p className="text-gray-600 mb-8 max-w-xl mx-auto" data-id="element-938">
                {t('talks.customDescription')}
              </p>
              <Link to="/contact" data-id="element-939">
                <Button variant="secondary" rightIcon={<ArrowRight size={18} data-id="element-941" />} data-id="element-940">
                  {t('talks.customCta')}
                </Button>
              </Link>
            </Card>
          </Animate>
        </section>
      </div>
    </PageTransition>;
}